import {patchState, signalStore, withComputed, withMethods, withState} from '@ngrx/signals';
import {computed, inject} from '@angular/core';
import {AuthenticationService} from '../../service/authentication.service';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {exhaustMap} from 'rxjs';
import {tap} from 'rxjs/operators';
import * as UserSettingsActions from './user-settings/user-settings.actions';
import {Store} from '@ngrx/store';
import * as fromRoot from '../app.reducers';

export type UserState = {
  userInfo: UserInfoDto;
  locale: string;
};

const initialState: UserState = {
  userInfo: undefined,
  locale: 'en',
};

export const UserSignalStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  withMethods((store, authenticationService = inject(AuthenticationService), appStore = inject(Store<fromRoot.AppState>)) => ({
    fetchUserInfo: rxMethod<void>(
      exhaustMap(() => {
        return authenticationService.getUserData().pipe(
          tap((userInfo) => {
            appStore.dispatch(new UserSettingsActions.UpdateUserSettingsAction(userInfo.id));
            patchState(store, {...store.userInfo, userInfo});
          })
        );
      })
    ),
    updateUserLocale(locale: string): void {
      patchState(store, {locale: locale});
    }
  })),
  withComputed(({ userInfo }) => ({
    isDevPoi: computed(() => userInfo()?.roles.includes(<Role>'POI_DEV')),
    isSupportPoi: computed(() => userInfo()?.roles.includes(<Role>'POI_SUPPORT')),
    isSupportPb: computed(() => userInfo()?.roles.includes(<Role>'PBV_SUPPORT')),

    isAdminPb: computed(() => userInfo()?.roles.includes(<Role>'PBV_ADMIN')),
    isAdminPia: computed(() => userInfo()?.roles.includes(<Role>'PIA_ADMIN')),
    isAdminAllUc: computed(() => userInfo()?.roles.includes(<Role>'ALL_UC_ADMIN')),
    isAdminDinBil: computed(() => userInfo()?.roles.includes(<Role>'DIN_BIL_ADMIN')),
    isAdminVgrx: computed(() => userInfo()?.roles.includes(<Role>'VGRX_ADMIN')),

    isAuctionSellerPia: computed(() => userInfo()?.roles.includes(<Role>'PIA_SELLER_AUCTION')),
    isAuctionSellerPb: computed(() => userInfo()?.roles.includes(<Role>'PBV_SELLER_AUCTION')),
    isAuctionSellerAllUc: computed(() => userInfo()?.roles.includes(<Role>'ALL_UC_SELLER_AUCTION')),
    isAuctionSellerDinBil: computed(() => userInfo()?.roles.includes(<Role>'DIN_BIL_SELLER_AUCTION')),
    isAuctionSellerVgrx: computed(() => userInfo()?.roles.includes(<Role>'VGRX_SELLER_AUCTION')),

    isBuyNowSellerPia: computed(() => userInfo()?.roles.includes(<Role>'PIA_SELLER_BUYNOW')),
    isBuyNowSellerPb: computed(() => userInfo()?.roles.includes(<Role>'PBV_SELLER_BUYNOW')),
    isBuyNowSellerAllUc: computed(() => userInfo()?.roles.includes(<Role>'ALL_UC_SELLER_BUYNOW')),
    isBuyNowSellerDinBil: computed(() => userInfo()?.roles.includes(<Role>'DIN_BIL_SELLER_BUYNOW')),
    isBuyNowSellerVgrx: computed(() => userInfo()?.roles.includes(<Role>'VGRX_SELLER_BUYNOW')),

    isBuyerPia: computed(() => userInfo()?.roles.includes(<Role>'PIA_BUYER')),
    isBuyerPb: computed(() => userInfo()?.roles.includes(<Role>'PBV_BUYER')),
    isBuyerAllUc: computed(() => userInfo()?.roles.includes(<Role>'ALL_UC_BUYER')),
    isBuyerDinBil: computed(() => userInfo()?.roles.includes(<Role>'DIN_BIL_BUYER')),
    isBuyerVgrx: computed(() => userInfo()?.roles.includes(<Role>'VGRX_BUYER')),
    isBuyerEnforcedAuction: computed(() => userInfo()?.roles.includes(<Role>'PIA_GW_BUYER_ENFORCED_AUCTION')),
    isViewerEnforcedAuction: computed(() => userInfo()?.roles.includes(<Role>'PIA_GW_VIEWER_ENFORCED_AUCTION')),

    isExaminerPia: computed(() => userInfo()?.roles.includes(<Role>'PIA_EXAMINER')),
    isCustodianPb: computed(() => userInfo()?.roles.includes(<Role>'PBV_CUSTODIAN')),
    isSurveyorPb: computed(() => userInfo()?.roles.includes(<Role>'PBV_SURVEYOR')),
    isClerkPb: computed(() => userInfo()?.roles.includes(<Role>'PBV_CLERK')),
    isClerkBmiPb: computed(() => userInfo()?.roles.includes(<Role>'PBV_CLERK_BMI')),

    isAustrian: computed(() => userInfo()?.country === 'AT'),
    isGerman: computed(() => userInfo()?.country === 'DE'),
  })),
  withComputed((store) => ({
    hasAnyRoleForPia: computed(() => store.isDevPoi() || store.isSupportPoi() || store.isAdminPia() || store.isAuctionSellerPia() ||
      store.isBuyNowSellerPia() || store.isBuyerPia() || store.isExaminerPia() || store.isBuyerEnforcedAuction() ||
      store.isViewerEnforcedAuction()),
    hasAnyRoleForPB: computed(() => store.isDevPoi() || store.isSupportPoi() ||
      store.isSupportPb() || store.isAdminPb() || store.isAuctionSellerPb() || store.isBuyNowSellerPb() || store.isBuyerPb() ||
      store.isCustodianPb() || store.isSurveyorPb() || store.isClerkPb() || store.isClerkBmiPb()),
    hasAnyBuyerRoleOrAdmin: computed(() => store.isDevPoi() || store.isSupportPoi() || store.isSupportPb() ||
      store.isAdminPb() || store.isAdminAllUc() || store.isAdminPia() || store.isAdminDinBil() || store.isAdminVgrx() ||
      store.isBuyerPb() || store.isBuyerAllUc() || store.isBuyerPia() || store.isBuyerDinBil() || store.isBuyerVgrx() ||
      store.isBuyerEnforcedAuction()),
    hasAnyRoleForAllUc: computed(() => store.isDevPoi() || store.isSupportPoi() ||
      store.isAdminAllUc() || store.isAuctionSellerAllUc() || store.isBuyNowSellerAllUc() || store.isBuyerAllUc()),
    hasAnyRoleForDinBil: computed(() => store.isDevPoi() || store.isSupportPoi() ||
      store.isAdminDinBil() || store.isAuctionSellerDinBil() || store.isBuyNowSellerDinBil() || store.isBuyerDinBil()),
    hasAnyRoleForVgrx: computed(() => store.isDevPoi() || store.isSupportPoi() ||
      store.isAdminVgrx() || store.isAuctionSellerVgrx() || store.isBuyNowSellerVgrx() || store.isBuyerVgrx()),

    // HAS ANY BUYING ROLE FOR CHANNEL (used e.g. in most buying views)

    canBuyOffersPia: computed(() => store.isDevPoi() || store.isSupportPoi() || store.isAdminPia() ||
      store.isBuyerPia() || store.isBuyerEnforcedAuction() || store.isViewerEnforcedAuction()),
    canBuyOffersPb: computed(() => store.isDevPoi() || store.isSupportPoi() || store.isAdminPb() || store.isSupportPb() || store.isBuyerPb()),
    canBuyOffersAllUc: computed(() => store.isDevPoi() || store.isSupportPoi() || store.isAdminAllUc() || store.isBuyerAllUc()),
    canBuyOffersDinBil: computed(() => store.isDevPoi() || store.isSupportPoi() || store.isAdminDinBil() || store.isBuyerDinBil()),
    canBuyOffersVgrx: computed(() => store.isDevPoi() || store.isSupportPoi() || store.isAdminVgrx() || store.isBuyerVgrx()),

    // HAS ANY SELLING ROLE FOR CHANNEL (used e.g. in most selling views)

    canSellOffersPia: computed(() => store.isDevPoi() || store.isSupportPoi() || store.isAdminPia() || store.isAuctionSellerPia() ||
      store.isBuyNowSellerPia() || store.isViewerEnforcedAuction()),
    canSellOffersPb: computed(() => store.isDevPoi() || store.isSupportPoi() || store.isAdminPb() || store.isSupportPb() ||
      store.isAuctionSellerPb() || store.isBuyNowSellerPb()),
    canSellOffersAllUc: computed(() => store.isDevPoi() || store.isSupportPoi() || store.isAdminAllUc() ||
      store.isAuctionSellerAllUc() || store.isBuyNowSellerAllUc()),
    canSellOffersDinBil: computed(() => store.isDevPoi() || store.isSupportPoi() || store.isAdminDinBil() ||
      store.isAuctionSellerDinBil() || store.isBuyNowSellerDinBil()),
    canSellOffersVgrx: computed(() => store.isDevPoi() || store.isSupportPoi() || store.isAdminVgrx() ||
      store.isAuctionSellerVgrx() || store.isBuyNowSellerVgrx()),

    // Further convenience flags
    isSupportOrDevPoi: computed(() => store.isSupportPoi() || store.isDevPoi()),
  })),
  withMethods((store) => ({
    isAdminForChannel(channel: DistributionChannel): boolean {
      switch(channel) {
      case 'PIA':
        return store.isAdminPia();
      case 'PB':
        return store.isAdminPb();
      case 'ALL_UC':
        return store.isAdminAllUc();
      case 'DIN_BIL':
        return store.isAdminDinBil();
      case 'VGRX':
        return store.isAdminVgrx();
      default: return false;
      }
    },
    canSellOffers(channel: DistributionChannel): boolean {
      switch (channel) {
      case 'PIA':
        return store.canSellOffersPia();
      case 'PB':
        return store.canSellOffersPb();
      case 'ALL_UC':
        return store.canSellOffersAllUc();
      case 'DIN_BIL':
        return store.canSellOffersDinBil();
      case 'VGRX':
        return store.canSellOffersVgrx();
      default:
        return false;
      }
    }
  })),
);
